import { useRef, useCallback } from 'react'

/**
 * Custom hook for managing tab scrolling functionality
 * @param {Object} options - Configuration options
 * @param {number} options.scrollDelay - Delay before scrolling (default: 100ms)
 * @param {number} options.mountDelay - Delay on mount (default: 200ms)
 * @returns {Object} Hook utilities and functions
 */
export const useTabScrolling = (options = {}) => {
  const { scrollDelay = 100, mountDelay = 200 } = options
  const wrapperRef = useRef(null)
  const tabRefs = useRef({})

  /**
   * Scroll to a specific tab by centering it in the viewport
   * @param {string|number} tabId - The ID of the tab to scroll to
   */
  const scrollToActiveTab = useCallback(tabId => {
    const tabElement = tabRefs.current[tabId]
    const wrapperElement = wrapperRef.current

    if (tabElement && wrapperElement) {
      const wrapperRect = wrapperElement.getBoundingClientRect()
      const tabRect = tabElement.getBoundingClientRect()

      // Calculate scroll position to center the active tab
      const scrollLeft =
        wrapperElement.scrollLeft +
        (tabRect.left - wrapperRect.left) -
        wrapperRect.width / 2 +
        tabRect.width / 2

      wrapperElement.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      })
    }
  }, [])

  /**
   * Scroll to active tab with a delay
   * @param {string|number} tabId - The ID of the tab to scroll to
   * @param {number} delay - Custom delay (optional)
   */
  const scrollToActiveTabWithDelay = useCallback(
    (tabId, delay = scrollDelay) => {
      setTimeout(() => {
        scrollToActiveTab(tabId)
      }, delay)
    },
    [scrollToActiveTab, scrollDelay]
  )

  /**
   * Scroll to active tab on mount with delay
   * @param {string|number} tabId - The ID of the tab to scroll to
   */
  const scrollToActiveTabOnMount = useCallback(
    tabId => {
      setTimeout(() => {
        scrollToActiveTab(tabId)
      }, mountDelay)
    },
    [scrollToActiveTab, mountDelay]
  )

  /**
   * Register a tab element reference
   * @param {string|number} tabId - The ID of the tab
   * @param {HTMLElement} element - The DOM element
   */
  const registerTabRef = useCallback((tabId, element) => {
    if (element) {
      tabRefs.current[tabId] = element
    }
  }, [])

  /**
   * Clear all tab references
   */
  const clearTabRefs = useCallback(() => {
    tabRefs.current = {}
  }, [])

  return {
    wrapperRef,
    tabRefs,
    scrollToActiveTab,
    scrollToActiveTabWithDelay,
    scrollToActiveTabOnMount,
    registerTabRef,
    clearTabRefs,
  }
}

export default useTabScrolling
